import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getEmployees, getSalary, deleteWorkEntry, verifySalaryMonth, paySalaryMonth, reopenSalaryMonth, exportSalary, exportSalaryPdf, sendSalaryPdf, createPayrollAdjustment, deletePayrollAdjustment, downloadBlob } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { SendPdfDialog } from '../../components/SendPdfDialog';
import { SalaryTaxBreakdown } from '../../components/SalaryTaxBreakdown';
import { SalaryLinesTable } from '../../components/salary/SalaryLinesTable';
import { CompanyCostsCard } from '../../components/salary/CompanyCostsCard';
import { ManualWorkEntryForm } from '../../components/salary/ManualWorkEntryForm';
import { ADJUSTMENT_TYPES, SALARY_STATUS, formatDate, formatEur } from '../../lib/workStatus';
import { Wallet, Lock, Unlock, Download, Trash2, AlertTriangle, FileDown, Mail, ShieldCheck, BadgeCheck } from 'lucide-react';
import { toast } from 'sonner';

const now = new Date();
const MONTHS = Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: new Date(2024, i, 1).toLocaleString('lv', { month: 'long' }) }));
const adjLabel = (type) => ADJUSTMENT_TYPES.find((a) => a.value === type)?.label || type;

const AdjustmentForm = ({ employeeId, year, month, disabled, onSaved }) => {
  const [form, setForm] = useState({ adj_type: 'piemaksa', amount: '', hours: '', hourly_rate: '', note: '', date: '' });
  const defaultDate = `${year}-${String(month).padStart(2, '0')}-01`;

  const submit = async (e) => {
    e.preventDefault();
    try {
      await createPayrollAdjustment({
        employee_id: employeeId,
        date: `${form.date || defaultDate}T00:00:00`,
        adj_type: form.adj_type,
        amount: Number(form.amount || 0),
        hours: form.hours === '' ? null : Number(form.hours),
        hourly_rate: form.hourly_rate === '' ? null : Number(form.hourly_rate),
        note: form.note || null
      });
      toast.success('Pievienots');
      setForm({ adj_type: 'piemaksa', amount: '', hours: '', hourly_rate: '', note: '', date: '' });
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Neizdevās pievienot');
    }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end" data-testid="salary-adjustment-form">
      <div className="space-y-1">
        <Label className="text-xs">Tips</Label>
        <Select value={form.adj_type} onValueChange={(v) => setForm((p) => ({ ...p, adj_type: v }))} disabled={disabled}>
          <SelectTrigger data-testid="salary-adj-type"><SelectValue /></SelectTrigger>
          <SelectContent>{ADJUSTMENT_TYPES.map((a) => <SelectItem key={a.value} value={a.value} data-testid={`salary-adj-type-${a.value}`}>{a.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {form.adj_type === 'stundas' ? (
        <>
          <div className="space-y-1"><Label className="text-xs">Stundas</Label><Input type="number" step="0.5" value={form.hours} disabled={disabled} onChange={(e) => setForm((p) => ({ ...p, hours: e.target.value }))} data-testid="salary-adj-hours" /></div>
          <div className="space-y-1"><Label className="text-xs">€/h</Label><Input type="number" step="0.01" value={form.hourly_rate} disabled={disabled} onChange={(e) => setForm((p) => ({ ...p, hourly_rate: e.target.value }))} data-testid="salary-adj-hourly" /></div>
        </>
      ) : (
        <div className="space-y-1 md:col-span-2"><Label className="text-xs">Summa (€){form.adj_type === 'korekcija' ? ' (var būt negatīva)' : ''}</Label><Input type="number" step="0.01" value={form.amount} disabled={disabled} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} data-testid="salary-adj-amount" /></div>
      )}
      <div className="space-y-1"><Label className="text-xs">Datums</Label><Input type="date" value={form.date || defaultDate} disabled={disabled} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} data-testid="salary-adj-date" /></div>
      <div className="space-y-1"><Label className="text-xs">Piezīme</Label><Input value={form.note} disabled={disabled} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} data-testid="salary-adj-note" /></div>
      <Button type="submit" disabled={disabled} className="bg-[#1F3D2B] hover:bg-[#2a5039]" data-testid="salary-adj-submit">Pievienot</Button>
    </form>
  );
};

const StatusStepper = ({ status }) => {
  const steps = ['open', 'verified', 'paid'];
  const index = steps.indexOf(status);
  return (
    <div className="flex items-center gap-2 text-xs" data-testid="admin-salary-stepper">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <span className={`px-2.5 py-1 rounded-full font-medium ${i <= index ? SALARY_STATUS[s].className : 'bg-gray-100 text-gray-400'}`} data-testid={`admin-salary-step-${s}`}>{SALARY_STATUS[s].label}</span>
          {i < steps.length - 1 && <span className="text-gray-300">→</span>}
        </React.Fragment>
      ))}
    </div>
  );
};

const SalaryCalculation = () => {
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);

  const params = () => ({ employee_id: employeeId, year: Number(year), month: Number(month) });

  useEffect(() => {
    getEmployees().then((data) => { setEmployees(data || []); if (data?.length) setEmployeeId((prev) => prev || data[0].id); }).catch(() => toast.error('Neizdevās ielādēt darbiniekus'));
  }, []);

  const load = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      setSummary(await getSalary({ employee_id: employeeId, year: Number(year), month: Number(month) }));
    } catch (error) {
      toast.error('Neizdevās ielādēt aprēķinu');
    } finally {
      setLoading(false);
    }
  }, [employeeId, year, month]);

  useEffect(() => { load(); }, [load]);

  const runAction = async (action, confirmText, successText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    try {
      setSummary(await action(params()));
      toast.success(successText);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Darbība neizdevās');
    }
  };

  const verify = () => runAction(verifySalaryMonth, 'Atzīmēt mēnesi kā PĀRBAUDĪTU? Darba ieraksti un likmes tiks bloķēti, aprēķins tiks saglabāts nemainīgs.', 'Mēnesis pārbaudīts un bloķēts');
  const pay = () => runAction(paySalaryMonth, 'Atzīmēt algu kā IZMAKSĀTU?', 'Alga atzīmēta kā izmaksāta');
  const reopen = () => runAction(reopenSalaryMonth, summary?.status === 'paid'
    ? 'UZMANĪBU: alga jau atzīmēta kā izmaksāta. Tiešām atvērt mēnesi labošanai? Aprēķins tiks pārrēķināts.'
    : 'Atvērt mēnesi labošanai? Bloķējums tiks noņemts un aprēķins pārrēķināts.', 'Mēnesis atvērts labošanai');

  const exportFile = async (fn, ext) => {
    try {
      downloadBlob(await fn(params()), `alga_${year}_${month}.${ext}`);
    } catch (error) {
      toast.error('Neizdevās eksportēt');
    }
  };

  const removeEntry = async (entry) => {
    if (!window.confirm(`Dzēst ierakstu "${entry.label}" ${entry.amount} ${entry.unit} (${formatDate(entry.date)})?`)) return;
    try { await deleteWorkEntry(entry.id); load(); } catch (error) { toast.error(error.response?.data?.detail || 'Neizdevās dzēst'); }
  };

  const removeAdj = async (id) => {
    try { await deletePayrollAdjustment(id); load(); } catch (error) { toast.error(error.response?.data?.detail || 'Neizdevās dzēst'); }
  };

  const locked = Boolean(summary?.locked);
  const status = summary?.status || 'open';

  return (
    <div className="space-y-6" data-testid="admin-salary-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1F3D2B] flex items-center gap-2" data-testid="admin-salary-title"><Wallet className="w-6 h-6" /> Mēneša algas aprēķins</h1>
          <p className="text-[#2E2E2E]/70">Darba veids → apjoms → mērvienība → likme (spēkā darba dienā) → summa. Likmes pārvalda sadaļā <Link to="/portal/admin/work-types" className="underline text-[#1F3D2B]" data-testid="admin-salary-work-types-link">Darba veidi un likmes</Link>.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => exportFile(exportSalary, 'xlsx')} disabled={!summary} data-testid="admin-salary-export"><Download className="w-4 h-4 mr-2" /> XLSX</Button>
          <Button variant="outline" onClick={() => exportFile(exportSalaryPdf, 'pdf')} disabled={!summary} data-testid="admin-salary-export-pdf"><FileDown className="w-4 h-4 mr-2" /> PDF</Button>
          <Button variant="outline" onClick={() => setSendOpen(true)} disabled={!summary} data-testid="admin-salary-send"><Mail className="w-4 h-4 mr-2" /> Nosūtīt darbiniekam</Button>
          {status === 'open' && <Button onClick={verify} disabled={!summary} className="bg-[#1F3D2B] hover:bg-[#2a5039]" data-testid="admin-salary-verify"><ShieldCheck className="w-4 h-4 mr-2" /> Atzīmēt kā pārbaudītu</Button>}
          {status === 'verified' && <Button onClick={pay} className="bg-green-700 hover:bg-green-800" data-testid="admin-salary-pay"><BadgeCheck className="w-4 h-4 mr-2" /> Atzīmēt kā izmaksātu</Button>}
          {locked && <Button variant="outline" onClick={reopen} data-testid="admin-salary-reopen"><Unlock className="w-4 h-4 mr-2" /> Atvērt labošanai</Button>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="admin-salary-filters">
        <div className="space-y-1">
          <Label className="text-xs">Darbinieks</Label>
          <Select value={employeeId} onValueChange={setEmployeeId}>
            <SelectTrigger data-testid="admin-salary-employee"><SelectValue placeholder="Izvēlieties darbinieku" /></SelectTrigger>
            <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id} data-testid={`admin-salary-employee-${e.id}`}>{e.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Gads</Label>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger data-testid="admin-salary-year"><SelectValue /></SelectTrigger>
            <SelectContent>{[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Mēnesis</Label>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger data-testid="admin-salary-month"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m) => <SelectItem key={m.value} value={m.value} data-testid={`admin-salary-month-${m.value}`}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <SendPdfDialog
        open={sendOpen}
        onOpenChange={setSendOpen}
        title="Nosūtīt algas aprēķinu"
        description={`PDF algas lapa (${summary?.employee_name || ''}, ${MONTHS.find((m) => m.value === month)?.label} ${year}) tiks nosūtīta e-pastā.`}
        defaultEmail={employees.find((e) => e.id === employeeId)?.email || ''}
        onSend={(email, message) => sendSalaryPdf({ ...params(), email, message })}
        testId="admin-salary-send"
      />

      {loading || !summary ? (
        <div className="flex items-center justify-center h-40" data-testid="admin-salary-loading"><div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3 rounded-xl bg-white shadow-sm px-4 py-3" data-testid="admin-salary-status-bar">
            <StatusStepper status={status} />
            <Badge className={SALARY_STATUS[status]?.className} data-testid="admin-salary-status-badge">{locked && <Lock className="w-3 h-3 mr-1" />}{SALARY_STATUS[status]?.label || status}</Badge>
            {summary.verified_at && <span className="text-xs text-[#2E2E2E]/60" data-testid="admin-salary-verified-info">Pārbaudīja {summary.verified_by} · {formatDate(summary.verified_at)}</span>}
            {summary.paid_at && <span className="text-xs text-[#2E2E2E]/60" data-testid="admin-salary-paid-info">Izmaksāts {formatDate(summary.paid_at)} ({summary.paid_by})</span>}
            {locked && <span className="text-xs text-teal-800 ml-auto" data-testid="admin-salary-locked-note">Ieraksti un likmes bloķētas — aprēķins fiksēts.</span>}
          </div>

          {(summary.pending_entries > 0 || summary.needs_fix_entries > 0) && (
            <Link to="/portal/admin/work-entries" className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-800 text-sm" data-testid="admin-salary-pending-warning">
              <AlertTriangle className="w-5 h-5" /> {summary.pending_entries} neapstiprināti un {summary.needs_fix_entries} labojami ieraksti šajā mēnesī netiek iekļauti aprēķinā. Atveriet darba atskaites, lai apstiprinātu.
            </Link>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-salary-summary">
            <Card className="border-0 shadow-sm"><CardContent className="p-4"><p className="text-xs text-[#2E2E2E]/60">Bruto alga</p><p className="text-2xl font-bold text-[#1F3D2B]" data-testid="admin-salary-gross">{formatEur(summary.gross_total)}</p><p className="text-xs text-[#2E2E2E]/50">darbi {formatEur(summary.work_total)} + piemaksas <span data-testid="admin-salary-additions">{formatEur(summary.additions_total)}</span></p></CardContent></Card>
            <Card className="border-0 shadow-sm"><CardContent className="p-4"><p className="text-xs text-[#2E2E2E]/60">Nodokļi (VSAOI + IIN)</p><p className="text-2xl font-bold text-red-700" data-testid="admin-salary-taxes">{formatEur((summary.taxes?.employee_vsaoi || 0) + (summary.taxes?.iin || 0))}</p><p className="text-xs text-[#2E2E2E]/50">neto {formatEur(summary.net_total)}</p></CardContent></Card>
            <Card className="border-0 shadow-sm bg-[#1F3D2B] text-white"><CardContent className="p-4"><p className="text-xs text-white/70">Izmaksāt darbiniekam (− avansi {formatEur(summary.advances_total)})</p><p className="text-2xl font-bold" data-testid="admin-salary-payout">{formatEur(summary.payout_total)}</p></CardContent></Card>
            <Card className="border-0 shadow-sm"><CardContent className="p-4"><p className="text-xs text-[#2E2E2E]/60">Uzņēmuma kopējās izmaksas</p><p className="text-2xl font-bold text-[#1F3D2B]" data-testid="admin-salary-employer-cost">{formatEur(summary.employer_total_cost)}</p><p className="text-xs text-[#2E2E2E]/50">bruto + DD VSAOI + riska nodeva</p></CardContent></Card>
          </div>

          <h2 className="text-sm font-bold uppercase tracking-wide text-[#1F3D2B]/70 pt-2" data-testid="admin-salary-section-1">1. Darba samaksa</h2>

          <ManualWorkEntryForm employeeId={employeeId} year={Number(year)} month={Number(month)} disabled={locked} onSaved={setSummary} />

          <SalaryLinesTable summary={summary} onDeleteEntry={locked ? null : removeEntry} />

          <Card className="border-0 shadow-sm" data-testid="admin-salary-adjustments">
            <CardHeader><CardTitle className="text-base">Piemaksas, prēmijas, atvaļinājums / slimības lapa, avansi, korekcijas</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <AdjustmentForm employeeId={employeeId} year={Number(year)} month={Number(month)} disabled={locked} onSaved={load} />
              {summary.adjustments.length === 0 ? <p className="text-sm text-[#2E2E2E]/60" data-testid="admin-salary-adjustments-empty">Nav pievienotu korekciju.</p> : (
                <div className="divide-y">
                  {summary.adjustments.map((adj) => (
                    <div key={adj.id} className="flex items-center justify-between py-2 text-sm" data-testid={`admin-salary-adj-${adj.id}`}>
                      <div><Badge className={adj.adj_type === 'avanss' ? 'bg-red-100 text-red-700' : adj.adj_type === 'korekcija' && adj.amount < 0 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>{adjLabel(adj.adj_type)}</Badge> <span className="ml-2 text-[#2E2E2E]/70">{formatDate(adj.date)}{adj.note ? ` · ${adj.note}` : ''}{adj.adj_type === 'stundas' && adj.hours ? ` · ${adj.hours} h × ${formatEur(adj.hourly_rate)}` : ''}</span></div>
                      <div className="flex items-center gap-2"><span className="font-semibold">{adj.adj_type === 'avanss' ? '−' : ''}{formatEur(adj.amount)}</span>{!locked && <Button size="sm" variant="ghost" onClick={() => removeAdj(adj.id)} data-testid={`admin-salary-adj-delete-${adj.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>}</div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-between items-center rounded-xl bg-[#f8faf8] px-6 py-3 font-bold text-[#1F3D2B]" data-testid="admin-salary-gross-row"><span>BRUTO ALGA</span><span>{formatEur(summary.gross_total)}</span></div>

          <SalaryTaxBreakdown summary={summary} />

          <CompanyCostsCard summary={summary} />
        </>
      )}
    </div>
  );
};

export default SalaryCalculation;
